import React from 'react';
import {
  AlertTriangle, Clock, Copy, Eraser, KeyRound, Loader2, Play, Shield, Trash2,
} from 'lucide-react';
import { openapiApi } from '@/config/api';
import { useAuth } from '@/context/AuthContext';
import {
  clearPlaygroundAuth,
  getPublicState,
  getSecretsForExecute,
  setApiKey,
  setAuthMode,
  setBearerToken,
  setGatewayApiKey,
  subscribePlaygroundAuth,
} from './playgroundAuthStore';
import { buildSanitizedCurl, httpStatusHint } from './buildSanitizedCurl';

function MethodBadge({ method }) {
  const tone = {
    GET: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    POST: 'bg-blue-50 text-blue-800 border-blue-200',
    PUT: 'bg-amber-50 text-amber-800 border-amber-200',
    PATCH: 'bg-orange-50 text-orange-800 border-orange-200',
    DELETE: 'bg-red-50 text-red-800 border-red-200',
  }[method] || 'bg-slate-50 text-slate-700 border-slate-200';
  return <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${tone}`}>{method}</span>;
}

function parseOperations(spec) {
  const ops = [];
  const paths = spec?.paths || {};
  Object.entries(paths).forEach(([path, item]) => {
    Object.entries(item || {}).forEach(([method, op]) => {
      if (!['get', 'post', 'put', 'patch', 'delete', 'head', 'options'].includes(method)) return;
      const mutating = !['get', 'head', 'options'].includes(method);
      ops.push({
        key: `${method.toUpperCase()} ${path}`,
        method: method.toUpperCase(),
        path,
        operationId: op.operationId || `${method}_${path}`,
        summary: op.summary || `${method.toUpperCase()} ${path}`,
        description: op.description || '',
        tags: op.tags || ['Other'],
        security: op.security,
        deprecated: Boolean(op.deprecated),
        mutating,
        parameters: op.parameters || [],
        requestBody: op.requestBody || null,
        responses: op.responses || {},
      });
    });
  });
  return ops.sort((a, b) => a.path.localeCompare(b.path) || a.method.localeCompare(b.method));
}

function exampleFromSchema(schema, depth = 0) {
  if (!schema || depth > 4) return null;
  if (schema.example !== undefined) return schema.example;
  if (schema.$ref) return {};
  if (schema.type === 'object' || schema.properties) {
    const out = {};
    Object.entries(schema.properties || {}).forEach(([k, v]) => {
      out[k] = exampleFromSchema(v, depth + 1);
    });
    return out;
  }
  if (schema.type === 'array') return [exampleFromSchema(schema.items, depth + 1)];
  if (schema.enum) return schema.enum[0];
  if (schema.type === 'integer' || schema.type === 'number') return 0;
  if (schema.type === 'boolean') return false;
  return schema.type === 'string' ? '' : null;
}

function bodyExample(requestBody) {
  const content = requestBody?.content?.['application/json'];
  if (!content) return '{\n  \n}';
  if (content.example) return JSON.stringify(content.example, null, 2);
  if (content.examples) {
    const first = Object.values(content.examples)[0];
    if (first?.value) return JSON.stringify(first.value, null, 2);
  }
  return JSON.stringify(exampleFromSchema(content.schema) || {}, null, 2);
}

export default function PrimeApiPlayground() {
  const { integratedToken, user } = useAuth();
  const [config, setConfig] = React.useState(null);
  const [spec, setSpec] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [authUi, setAuthUi] = React.useState(getPublicState());
  const [authMode, setAuthModeLocal] = React.useState(getSecretsForExecute().authMode);
  const [envId, setEnvId] = React.useState('development');
  const [connectionMode, setConnectionMode] = React.useState('native');
  const [query, setQuery] = React.useState('');
  const [tagFilter, setTagFilter] = React.useState('ALL');
  const [methodFilter, setMethodFilter] = React.useState('ALL');
  const [selectedKey, setSelectedKey] = React.useState('');
  const [pathParams, setPathParams] = React.useState({});
  const [queryParams, setQueryParams] = React.useState({});
  const [headerParams, setHeaderParams] = React.useState({});
  const [bodyText, setBodyText] = React.useState('{\n  \n}');
  const [bodyError, setBodyError] = React.useState('');
  const [executing, setExecuting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [bearerInput, setBearerInput] = React.useState('');
  const [apiKeyInput, setApiKeyInput] = React.useState('');
  const [gatewayKeyInput, setGatewayKeyInput] = React.useState('');
  const abortRef = React.useRef(null);

  React.useEffect(() => subscribePlaygroundAuth((s) => {
    setAuthUi(s);
    setAuthModeLocal(getSecretsForExecute().authMode);
  }), []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [cfgRes, specRes, histRes] = await Promise.all([
          openapiApi.get('/openapi/playground/config'),
          openapiApi.get('/openapi.json'),
          openapiApi.get('/openapi/playground/history').catch(() => ({ data: { data: { items: [] } } })),
        ]);
        if (cancelled) return;
        const cfg = cfgRes.data?.data;
        setConfig(cfg);
        setEnvId(cfg?.defaultEnvironmentId || 'development');
        setSpec(specRes.data);
        setHistory(histRes.data?.data?.items || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Failed to load playground');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const operations = React.useMemo(() => parseOperations(spec), [spec]);
  const tags = React.useMemo(() => {
    const set = new Set();
    operations.forEach((o) => o.tags.forEach((t) => set.add(t)));
    return [...set].sort();
  }, [operations]);

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return operations.filter((o) => {
      if (tagFilter !== 'ALL' && !o.tags.includes(tagFilter)) return false;
      if (methodFilter !== 'ALL' && o.method !== methodFilter) return false;
      if (!needle) return true;
      return [o.path, o.summary, o.operationId, ...(o.tags || [])].join(' ').toLowerCase().includes(needle);
    });
  }, [operations, query, tagFilter, methodFilter]);

  const selected = operations.find((o) => o.key === selectedKey) || null;

  React.useEffect(() => {
    if (!selected) return;
    const pp = {};
    const qp = {};
    const hp = {};
    (selected.parameters || []).forEach((p) => {
      if (p.in === 'path') pp[p.name] = '';
      if (p.in === 'query') qp[p.name] = p.schema?.default ?? '';
      if (p.in === 'header') hp[p.name] = '';
    });
    // also detect {param} in path
    const re = /\{([^}]+)\}/g;
    let m;
    while ((m = re.exec(selected.path))) {
      if (pp[m[1]] === undefined) pp[m[1]] = '';
    }
    setPathParams(pp);
    setQueryParams(qp);
    setHeaderParams(hp);
    setBodyText(selected.requestBody ? bodyExample(selected.requestBody) : '{\n  \n}');
    setBodyError('');
    setResult(null);
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const env = config?.environments?.find((e) => e.id === envId);
  const gateway = config?.gateway;
  const gatewayReady = connectionMode !== 'gateway' || gateway?.executionEnabled;
  const mutationsBlocked = envId === 'production' || env?.executionPolicy?.allowMutations === false;

  const applyAuth = () => {
    if (authMode === 'bearer') setBearerToken(bearerInput);
    if (authMode === 'apiKey') setApiKey(apiKeyInput);
    setGatewayApiKey(gatewayKeyInput);
    setBearerInput('');
    setApiKeyInput('');
    setGatewayKeyInput('');
  };

  const clearAuth = () => {
    clearPlaygroundAuth();
    setBearerInput('');
    setApiKeyInput('');
    setGatewayKeyInput('');
  };

  const onEnvChange = (next) => {
    setEnvId(next);
    clearAuth();
    setResult(null);
  };

  const formatBody = () => {
    try {
      setBodyText(JSON.stringify(JSON.parse(bodyText), null, 2));
      setBodyError('');
    } catch (e) {
      setBodyError(e.message || 'Invalid JSON');
    }
  };

  const execute = async () => {
    if (!selected) return;
    if (!gatewayReady) {
      setError('Gateway is not configured for execution. Use Native PRIME or contact the integration administrator.');
      return;
    }
    if (selected.mutating && mutationsBlocked) {
      setError('Mutation requests are blocked for this environment (production safety).');
      return;
    }
    if (selected.mutating) {
      const ok = window.confirm(`Confirm mutation ${selected.method} ${selected.path}? This may change data in ${envId}.`);
      if (!ok) return;
    }

    let parsedBody = undefined;
    if (selected.mutating || selected.requestBody) {
      if (bodyText.trim()) {
        try {
          parsedBody = JSON.parse(bodyText);
          setBodyError('');
        } catch (e) {
          setBodyError(e.message || 'Invalid JSON');
          return;
        }
      }
    }

    const secrets = getSecretsForExecute();
    let authMode = secrets.authMode;
    let bearerToken = secrets.bearerToken;
    let apiKey = secrets.apiKey;

    if (authMode === 'session') {
      authMode = 'bearer';
      bearerToken = integratedToken || '';
      if (!bearerToken) {
        setError('Authentication required — session JWT not available. Paste a Bearer token or re-login.');
        return;
      }
    } else if (authMode === 'bearer' && !bearerToken) {
      setError('Authentication required — provide a Bearer token and click Apply.');
      return;
    } else if (authMode === 'apiKey' && !apiKey) {
      setError('API Key required — provide x-api-key and click Apply.');
      return;
    }

    // JWT expiry hint (client-side decode of exp only — no secret verification)
    if (bearerToken) {
      try {
        const payload = JSON.parse(atob(bearerToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          setError('Token expired — clear authorization and sign in again.');
          return;
        }
      } catch {
        /* opaque / non-JWT key */
      }
    }

    setExecuting(true);
    setError('');
    setResult(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await openapiApi.post(
        '/openapi/playground/execute',
        {
          method: selected.method,
          pathTemplate: selected.path,
          pathParams,
          query: queryParams,
          headers: headerParams,
          body: parsedBody,
          contentType: 'application/json',
          environmentId: envId,
          connectionMode,
          operationId: selected.operationId,
          summary: selected.summary,
          authMode,
          bearerToken: authMode === 'bearer' ? bearerToken : undefined,
          apiKey: authMode === 'apiKey' ? apiKey : undefined,
          gatewayApiKey: connectionMode === 'gateway' ? secrets.gatewayApiKey || undefined : undefined,
          confirmedMutation: selected.mutating,
        },
        { signal: controller.signal }
      );
      setResult(res.data?.data || null);
      const hist = await openapiApi.get('/openapi/playground/history');
      setHistory(hist.data?.data?.items || []);
    } catch (e) {
      if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') {
        setError('Request cancelled.');
      } else {
        const msg = e.response?.data?.message || e.message || 'Request failed';
        const code = e.response?.data?.code;
        setError(code ? `${msg} (${code})` : msg);
        if (e.response?.data) {
          setResult({
            status: e.response.status,
            durationMs: null,
            bodyJson: e.response.data,
            environmentId: envId,
            connectionMode,
            method: selected.method,
            pathTemplate: selected.path,
            curl: buildSanitizedCurl({
              method: selected.method,
              url: `${env?.baseUrl || ''}${selected.path}`,
              authMode,
            }),
          });
        }
      }
    } finally {
      setExecuting(false);
      abortRef.current = null;
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Loading API Playground…</div>;
  }

  return (
    <div className="space-y-4">
      {/* Environment / connection */}
      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 lg:grid-cols-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Environment</label>
          <select className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={envId} onChange={(e) => onEnvChange(e.target.value)}>
            {(config?.environments || []).map((e) => (
              <option key={e.id} value={e.id} disabled={!e.configured}>{e.name}{!e.configured ? ' (Not Configured)' : ''}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500 break-all">{env?.baseUrl || env?.statusMessage || '—'}</p>
          <p className="mt-1 text-[11px] text-slate-500">
            Policy: {mutationsBlocked ? 'read-only / allowlist' : 'mutations allowed with confirmation'}
          </p>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Connection Mode</label>
          <select className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={connectionMode} onChange={(e) => setConnectionMode(e.target.value)}>
            <option value="native">Native PRIME</option>
            <option value="gateway" disabled={!gateway?.executionEnabled}>API Gateway{!gateway?.executionEnabled ? ' (Not Configured)' : ''}</option>
          </select>
          {!gateway?.executionEnabled && (
            <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {gateway?.statusMessage}
            </p>
          )}
          {gateway?.executionEnabled && gateway?.invocationUrl && (
            <p className="mt-1 break-all text-xs text-slate-500">Invocation: {gateway.invocationUrl}{gateway.pathPrefix || ''}</p>
          )}
          {gateway?.poc && (
            <p className="mt-1 text-[11px] text-slate-500">
              POC: <code className="font-mono">{gateway.poc.method} {gateway.poc.path}</code>
            </p>
          )}
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Authorization</label>
          <select
            className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm"
            value={authMode}
            onChange={(e) => { setAuthMode(e.target.value); setAuthModeLocal(e.target.value); }}
          >
            <option value="session">Current session JWT</option>
            <option value="bearer">Bearer token (paste)</option>
            <option value="apiKey">Channel API Key (x-api-key)</option>
          </select>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {authMode === 'session' && (
              <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 ${integratedToken ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                <Shield className="h-3 w-3" /> {integratedToken ? 'Session ready' : 'No session token'}
              </span>
            )}
            {authMode === 'bearer' && (
              <span className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1">{authUi.hasBearer ? 'Bearer set (memory)' : 'Bearer empty'}</span>
            )}
            {authMode === 'apiKey' && (
              <span className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1">{authUi.hasApiKey ? 'API key set (memory)' : 'API key empty'}</span>
            )}
            <button type="button" onClick={clearAuth} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 font-semibold hover:bg-slate-50">
              <Eraser className="h-3 w-3" /> Clear Authorization
            </button>
          </div>
        </div>
      </div>

      {/* Auth inputs — memory only */}
      {(authMode === 'bearer' || authMode === 'apiKey' || connectionMode === 'gateway') && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <KeyRound className="h-4 w-4" /> Authorize (memory only — not persisted)
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {authMode === 'bearer' && (
              <input
                type="password"
                autoComplete="off"
                className="h-10 rounded-md border border-slate-200 px-3 text-sm"
                placeholder="Paste Bearer JWT"
                value={bearerInput}
                onChange={(e) => setBearerInput(e.target.value)}
              />
            )}
            {authMode === 'apiKey' && (
              <input
                type="password"
                autoComplete="off"
                className="h-10 rounded-md border border-slate-200 px-3 text-sm"
                placeholder="Paste x-api-key"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
              />
            )}
            {connectionMode === 'gateway' && (
              <input
                type="password"
                autoComplete="off"
                className="h-10 rounded-md border border-slate-200 px-3 text-sm"
                placeholder="x-Gateway-APIKey (optional)"
                value={gatewayKeyInput}
                onChange={(e) => setGatewayKeyInput(e.target.value)}
              />
            )}
            <button type="button" onClick={applyAuth} className="h-10 rounded-md bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-800">
              Apply to session memory
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Tokens are never written to localStorage/sessionStorage by the playground. Cleared on logout.</p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-12">
        {/* Explorer */}
        <div className="space-y-3 lg:col-span-4">
          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
            <input className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm" placeholder="Search path, summary, operationId…" value={query} onChange={(e) => setQuery(e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <select className="h-9 rounded-md border border-slate-200 px-2 text-xs" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
                <option value="ALL">All modules</option>
                {tags.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="h-9 rounded-md border border-slate-200 px-2 text-xs" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
                <option value="ALL">All methods</option>
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="max-h-[520px] overflow-auto divide-y divide-slate-100">
              {filtered.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setSelectedKey(o.key)}
                  className={`flex w-full flex-col gap-1 px-2 py-2 text-left hover:bg-slate-50 ${selectedKey === o.key ? 'bg-slate-50' : ''}`}
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <MethodBadge method={o.method} />
                    {o.mutating ? <span className="text-[10px] font-bold text-amber-700">MUTATION</span> : <span className="text-[10px] font-bold text-emerald-700">READ</span>}
                    {o.deprecated && <span className="text-[10px] font-bold text-red-600">DEPRECATED</span>}
                    {(o.security === undefined || (Array.isArray(o.security) && o.security.length > 0)) && o.security !== null && (
                      <span className="text-[10px] text-slate-500">auth</span>
                    )}
                    {Array.isArray(o.security) && o.security.length === 0 && <span className="text-[10px] text-slate-400">public</span>}
                  </div>
                  <div className="truncate font-mono text-[11px] text-slate-800">{o.path}</div>
                  <div className="truncate text-[11px] text-slate-500">{o.summary}</div>
                </button>
              ))}
              {!filtered.length && <div className="p-4 text-center text-xs text-slate-500">No endpoints match filters.</div>}
            </div>
          </div>
        </div>

        {/* Builder + response */}
        <div className="space-y-3 lg:col-span-8">
          {!selected ? (
            <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Select an endpoint to build a request.</div>
          ) : (
            <>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <MethodBadge method={selected.method} />
                      <span className="font-mono text-sm font-semibold text-slate-900">{selected.path}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{selected.summary}</p>
                    <p className="mt-1 text-[11px] text-slate-400">operationId: {selected.operationId}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {executing && (
                      <button type="button" onClick={() => abortRef.current?.abort()} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">Cancel</button>
                    )}
                    <button
                      type="button"
                      disabled={executing || !env?.configured || !gatewayReady || (selected.mutating && mutationsBlocked)}
                      onClick={execute}
                      className="inline-flex items-center gap-2 rounded-md bg-teal-700 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
                    >
                      {executing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      Execute
                    </button>
                  </div>
                </div>

                {Object.keys(pathParams).length > 0 && (
                  <div className="mt-4">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Path parameters</div>
                    <div className="grid gap-2 md:grid-cols-2">
                      {Object.keys(pathParams).map((k) => (
                        <input key={k} className="h-9 rounded-md border border-slate-200 px-3 text-sm" placeholder={k} value={pathParams[k]} onChange={(e) => setPathParams((s) => ({ ...s, [k]: e.target.value }))} />
                      ))}
                    </div>
                  </div>
                )}

                {Object.keys(queryParams).length > 0 && (
                  <div className="mt-4">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Query parameters</div>
                    <div className="grid gap-2 md:grid-cols-2">
                      {Object.keys(queryParams).map((k) => (
                        <label key={k} className="text-xs text-slate-600">
                          {k}
                          <input className="mt-1 h-9 w-full rounded-md border border-slate-200 px-3 text-sm" value={queryParams[k]} onChange={(e) => setQueryParams((s) => ({ ...s, [k]: e.target.value }))} />
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {(selected.mutating || selected.requestBody) && (
                  <div className="mt-4">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Request body (JSON)</div>
                      <div className="flex gap-2">
                        <button type="button" onClick={formatBody} className="rounded border border-slate-200 px-2 py-1 text-xs font-semibold">Format JSON</button>
                        <button type="button" onClick={() => setBodyText(bodyExample(selected.requestBody))} className="rounded border border-slate-200 px-2 py-1 text-xs font-semibold">Reset example</button>
                      </div>
                    </div>
                    <textarea className="h-40 w-full rounded-md border border-slate-200 p-3 font-mono text-xs" value={bodyText} onChange={(e) => setBodyText(e.target.value)} spellCheck={false} />
                    {bodyError && <p className="mt-1 text-xs text-red-600">{bodyError}</p>}
                  </div>
                )}
              </div>

              {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

              {result && (
                <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`rounded-md border px-2 py-1 text-sm font-bold ${result.status >= 200 && result.status < 300 ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
                      HTTP {result.status ?? '—'}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Clock className="h-3.5 w-3.5" />{result.durationMs != null ? `${result.durationMs} ms` : '—'}</span>
                    <span className="text-xs text-slate-500">{result.environmentName || result.environmentId} · {result.connectionMode}</span>
                    {result.requestId && <span className="text-xs text-slate-400">req: {result.requestId}</span>}
                    {result.timestamp && <span className="text-xs text-slate-400">{result.timestamp}</span>}
                  </div>
                  {httpStatusHint(result.status) && (
                    <p className="text-xs text-slate-600">{httpStatusHint(result.status)}</p>
                  )}
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Response</div>
                      <button type="button" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600" onClick={() => copyText(JSON.stringify(result.bodyJson ?? result.bodyText, null, 2))}>
                        <Copy className="h-3 w-3" /> Copy JSON
                      </button>
                    </div>
                    <pre className="max-h-80 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-50">
                      {result.bodyJson ? JSON.stringify(result.bodyJson, null, 2) : (result.bodyText || '—')}
                    </pre>
                  </div>
                  {result.responseHeaders && (
                    <div>
                      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Response headers (safe)</div>
                      <pre className="max-h-32 overflow-auto rounded-md bg-slate-50 p-3 text-[11px] text-slate-700">{JSON.stringify(result.responseHeaders, null, 2)}</pre>
                    </div>
                  )}
                  {result.curl && (
                    <div>
                      <div className="mb-1 flex items-center justify-between">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">cURL (sanitized)</div>
                        <button type="button" className="inline-flex items-center gap-1 text-xs font-semibold" onClick={() => copyText(result.curl)}><Copy className="h-3 w-3" /> Copy</button>
                      </div>
                      <pre className="overflow-auto rounded-md bg-slate-50 p-3 text-[11px] text-slate-800">{result.curl}</pre>
                    </div>
                  )}
                  <button type="button" onClick={() => setResult(null)} className="text-xs font-semibold text-slate-600">Clear result</button>
                </div>
              )}
            </>
          )}

          {/* History */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">Request history (sanitized)</div>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600"
                onClick={() => { clearPlaygroundAuth(); window.dispatchEvent(new Event('prime-playground-clear-auth')); }}
                title="Clear in-memory auth"
              >
                <Trash2 className="h-3 w-3" /> Clear auth
              </button>
            </div>
            <p className="mb-3 text-[11px] text-slate-500">No Authorization, API keys, cookies, or bodies are stored. User: {user?.nama || user?.nip || '—'}</p>
            <div className="max-h-48 overflow-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-1 pr-2">Time</th>
                    <th className="py-1 pr-2">Method</th>
                    <th className="py-1 pr-2">Endpoint</th>
                    <th className="py-1 pr-2">Env</th>
                    <th className="py-1 pr-2">Mode</th>
                    <th className="py-1 pr-2">Status</th>
                    <th className="py-1">ms</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.requestId} className="border-t border-slate-100">
                      <td className="py-1.5 pr-2 whitespace-nowrap">{h.timestamp ? String(h.timestamp).replace('T', ' ').slice(0, 19) : '—'}</td>
                      <td className="py-1.5 pr-2"><MethodBadge method={h.method} /></td>
                      <td className="py-1.5 pr-2 font-mono">{h.pathTemplate}</td>
                      <td className="py-1.5 pr-2">{h.environmentId}</td>
                      <td className="py-1.5 pr-2">{h.connectionMode}</td>
                      <td className="py-1.5 pr-2">{h.statusCode ?? '—'}</td>
                      <td className="py-1.5">{h.durationMs ?? '—'}</td>
                    </tr>
                  ))}
                  {!history.length && (
                    <tr><td colSpan={7} className="py-4 text-center text-slate-500">No history yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
